import { Business } from "@turizi/shared";
import { IconSearch, IconSelector } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TagIcon } from "@/lib/tag-utils";
import CardModal from "./card-modal";
import { Link } from "react-router-dom";
import { BUSINESSES_PATH } from "@/lib/navigation";
import CreateBusinessComponent from "./create-new-business-component";

interface Props {
    onSelect: (item: Business) => void;
    onSelectMultiple?: (items: Business[]) => void;
    className?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    variant?: 'default' | 'unstyled';
    selected?: Business | null;
    placeholder?: JSX.Element;
    owned_by_me?: boolean | undefined;
    loading?: boolean;
    disabled?: boolean;
    exclude?: number[]; //IDs to exclude from the list
    allowedIds?: number[]; // IDs to limit selection to
    showIcon?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    autoFocus?: boolean;
    opened?: boolean;
    title?: React.ReactNode;
    modalSize?: string;
}

export default function BusinessSelector({
    variant = 'default',
    onSelect,
    className,
    size = 'sm',
    selected,
    placeholder,
    owned_by_me = undefined,
    loading = false,
    disabled = false,
    showIcon = true,
    onOpen,
    onClose,
    opened,
    title,
    modalSize,
}: Props) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [localOpened, setLocalOpened] = useState(false);

    const isModalOpened = opened !== undefined ? opened : localOpened;

    const handleClose = () => {
        if (opened === undefined) {
            setLocalOpened(false);
        }
        onClose?.();
    };

    const handleSelect = (item: Business) => {
        onSelect(item);
        setSearchQuery('');
        handleClose();
    };

    const selectedLink = selected
        ? `${BUSINESSES_PATH}/${selected?.id}`
        : '';

    return (
        <>
            {opened === undefined && (
                <div
                    className="w-full"
                    onClick={() => {
                        if (!disabled && !loading) {
                            setSearchQuery('');
                            setLocalOpened(true);
                            onOpen?.();
                        }
                    }}
                >
                    <div
                        className={`
                            ${className || 'w-full'}
                            ${variant === 'default'
                                ? disabled ? 'solid-box-border-tinted' : 'solid-box-border-hover'
                                : null
                            }
                            rounded-md transition-all
                            text-${size}
                            hover:text-black
                            h-10 py-2 px-1
                            flex items-center justify-between
                        `}
                    >
                        {!selected && (
                            <div className="flex-1">
                                {
                                    placeholder ||
                                    (
                                        <div className="flex items-center gap-2">
                                            <IconSearch size={20} stroke={1} color="gray" />
                                            <div className="text-sm text-gray-400">{t('select_client') + '...'}</div>
                                        </div>
                                    )
                                }
                            </div>
                        )}
                        {selected && (
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                {showIcon && (
                                    <div className="text-xxs flex flex-col items-center text-gray-600 px-2">
                                        <TagIcon name={selected.category} hideText noBackground size="xs" />
                                        #{selected.id}
                                    </div>
                                )}
                                <Link to={selectedLink}>
                                    {selected.name}
                                </Link>
                            </div>
                        )}
                        {!disabled && <IconSelector size={16} stroke={1} className="text-gray-700 flex-shrink-0" />}
                    </div>
                </div>
            )}

            <CardModal
                size={modalSize || "820px"}
                withCloseButton={true}
                opened={isModalOpened}
                onClose={handleClose}
                header={
                    title || (
                        <div>
                            {t('create_select_new_business')}
                        </div>
                    )
                }
            >
                <CreateBusinessComponent
                    preloadName={searchQuery}
                    onCreate={handleSelect}
                    onClose={handleClose}
                    owner={owned_by_me}
                />
            </CardModal>
        </>
    );
}